import { Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { setDoc, useDoc } from '../store/store';
import { situacaoViagem, viagemNoDia } from '../modulos/viagens';
import { C, alpha, useCores } from './theme';
import { fusoAparelho, horaEm, instante, nomeFuso } from '../lib/fuso';
import { hm, horaTela } from '../lib/date';
import { t } from '../lib/i18n';
import { abrirRota } from '../lib/mapa';
import { parteLigada } from '../lib/partes';

/** Durante uma viagem, o roteiro do dia entra na linha do tempo do My Planner.
 *  Com "Só o roteiro", as outras tarefas do dia ficam escondidas até a viagem acabar. */
export function useViagemDoDia(dia: string) {
  const doc = useDoc('viagens');
  const aj = useDoc('ajustes');
  const { liberada } = useCores();
  if (!liberada('viagens') || !parteLigada(aj.partesOcultas, 'viagens', 'viagem')) return { viagem: null, so: false };
  const viagem = (doc?.regs ?? []).find(r => viagemNoDia(r, dia)) ?? null;
  return { viagem, so: !!viagem && !!aj.viagemSo };
}

export function ViagemHoje({ dia, escondidas }: { dia: string; escondidas: number }) {
  const router = useRouter();
  const doc = useDoc('viagens');
  const aj = useDoc('ajustes');
  const { viagem, so } = useViagemDoDia(dia);
  const { corDe } = useCores();
  if (!viagem) return null;
  const cor = corDe('viagens');
  const regs = (doc?.regs ?? []).filter(r => parteLigada(aj.partesOcultas, 'viagens', r.col));   // D1: parte desligada não aparece
  const tz = viagem.c.fuso as string | undefined;
  const aqui = fusoAparelho();
  // hora mostrada: a do lugar (fuso da atividade) e, se for diferente, a do aparelho
  const itens = [
    ...regs.filter(r => r.pai === viagem.id && r.col === 'roteiro' && r.c.data === dia).map(r => ({ r, icone: '🗺️', titulo: r.c.atividade as string, hora: r.c.hora as string, local: r.c.local as string, fuso: (r.c.fuso || tz) as string })),
    ...regs.filter(r => r.pai === viagem.id && r.col === 'reserva' && r.c.data === dia).map(r => ({ r, icone: '🎫', titulo: [r.c.tipo && t(r.c.tipo), r.c.nome].filter(Boolean).join(' · '), hora: r.c.hora as string, local: r.c.codigo ? `#${r.c.codigo}` : r.c.endereco, fuso: (r.c.fuso || tz) as string })),
  ].sort((a, b) => String(a.hora || '99').localeCompare(String(b.hora || '99')));
  const abrir = (id?: string) => router.push({ pathname: '/aba/[id]', params: { id: 'viagens', item: id ?? viagem.id } } as any);
  return (
    <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 12, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.45) }}>
      <Pressable onPress={() => abrir()} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 24 }}>✈️</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>{viagem.c.destino}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{situacaoViagem(viagem, dia)}{tz && tz !== aqui ? ` · 📍 ${horaEm(tz)} ${nomeFuso(tz)}` : ''}</Text>
        </View>
      </Pressable>
      {!itens.length && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Nada no roteiro deste dia.')}</Text>}
      {itens.map(({ r, icone, titulo, hora, local, fuso }) => {
        const outro = hora && fuso && fuso !== aqui ? horaTela(hm(instante(dia, hora, fuso))) : null;
        return (
          <Pressable key={r.id} onPress={() => abrir(r.id)} style={{ flexDirection: 'row', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
            <Text style={{ color: cor, fontWeight: '800', width: 48 }}>{hora ? horaTela(hora) : '—'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600' }}>{icone} {titulo}</Text>
              {(!!local || !!outro) && <Text style={{ color: C.sub, fontSize: 12 }}>{[local, outro && t('{h} no seu relógio', { h: outro })].filter(Boolean).join(' · ')}</Text>}
              {!!local && icone === '🗺️' && <Pressable onPress={() => abrirRota(`${local}${viagem.c.destino ? ', ' + viagem.c.destino : ''}`)} hitSlop={6}><Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>🗺️ {t('Rota')}</Text></Pressable>}
            </View>
          </Pressable>
        );
      })}
      <Pressable onPress={() => setDoc('ajustes', d => ({ ...d, viagemSo: !d.viagemSo }))} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 16, color: so ? cor : C.sub }}>{so ? '☑' : '☐'}</Text>
        <Text style={{ color: C.sub, fontSize: 12, flex: 1 }}>{so ? t('Mostrando só o roteiro. {n} tarefa(s) do dia escondida(s).', { n: escondidas }) : t('Mostrar só o roteiro durante a viagem')}</Text>
      </Pressable>
    </View>
  );
}
